/**
 * DashboardPage — shared across Super, Admin, TL roles.
 *
 * Header row:  Title + subtitle
 * Stats row:   Total · In Progress · Overdue · Done  (compact chips)
 * Filter row:  [N tasks] [Employee ▼] [Status ▼] [Priority ▼] [⊞ Columns ▼] [Archive] [+ Add]
 *
 * Active view (default): excludes done tasks.
 * Archive view: shows only done tasks.
 *
 * Column visibility + Task-column width managed via useColumnPrefs (localStorage).
 * Task creation is optimistic — the inserted row (with joins) is upserted
 * straight into the list, so it renders immediately.
 */

import { useMemo, useState } from 'react';
import AppShell from '../../components/layout/AppShell.jsx';
import TaskTable from '../../components/task/Tasktable.jsx';
import AddTaskModal from '../../components/task/Addtaskmodal.jsx';
import RemarkModal from '../../components/task/Remarkmodal.jsx';
import { useTasks } from '../../hooks/usetasks.js';
import { useAuth } from '../../context/AuthContext.jsx';
import Icon from '../../components/ui/Icons.jsx';
import StatCard from '../../components/ui/StatCard.jsx';
import ColumnsDropdown from '../../components/ui/ColumnsDropdown.jsx';
import { TASK_STATUS_LABELS } from '../../data/constants.js';
import { useColumnPrefs } from '../../hooks/useColumnPrefs.js';
import { canAddRemarks, canDeleteTask } from '../../utils/roleUtils.js';
import { htmlToText } from '../../utils/sanitizeHtml.js';
import { supabase } from '../../api/supabase.js';

/* ── Status options for ACTIVE view (done excluded) ── */
const STATUS_OPTS_ACTIVE = [
  { value: 'all',         label: 'All statuses'                 },
  { value: 'todo',        label: TASK_STATUS_LABELS.todo        },
  { value: 'in_progress', label: TASK_STATUS_LABELS.in_progress },
  { value: 'overdue',     label: TASK_STATUS_LABELS.overdue     },
  { value: 'redo',        label: TASK_STATUS_LABELS.redo        },
];

const PRIORITY_OPTS = [
  { value: 'all',    label: 'All priorities' },
  { value: 'low',    label: 'Low'            },
  { value: 'medium', label: 'Medium'         },
  { value: 'high',   label: 'High'           },
];

/* ── Page ──────────────────────────────────────────────────── */
export default function DashboardPage() {
  const { profile } = useAuth();
  const { tasks, loading, error, upsertTask, patchTask, removeTask } = useTasks();

  const [addOpen,        setAddOpen]        = useState(false);
  const [remarkTask,     setRemarkTask]     = useState(null);
  const [isArchive,      setIsArchive]      = useState(false);
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [ownerFilter,    setOwnerFilter]    = useState('all');

  const colPrefs = useColumnPrefs('dashboard', true);

  /* Reset status filter when switching views */
  const handleViewSwitch = (archive) => {
    setIsArchive(archive);
    setStatusFilter('all');
  };

  /* TL+ row actions */
  const allowRemark = canAddRemarks(profile?.role);
  const allowDelete = canDeleteTask(profile?.role);

  const handleDelete = async (task) => {
    const title = htmlToText(task.title) || 'this task';
    if (!window.confirm(`Delete “${title}”? This cannot be undone.`)) return;
    removeTask(task.id);
    // .select() confirms a row was actually deleted — an RLS-filtered
    // no-op returns zero rows with no error, which would silently lie.
    const { data, error: deleteError } = await supabase
      .from('tasks')
      .delete()
      .eq('id', task.id)
      .select('id');
    if (deleteError || !data?.length) {
      if (deleteError) console.error('[ORIN] Failed to delete task:', deleteError.message);
      upsertTask(task); // restore the row — the delete didn't happen
    }
  };

  /* Split tasks into active / archive */
  const activeTasks   = useMemo(() => tasks.filter((t) => t.status !== 'done'), [tasks]);
  const archivedTasks = useMemo(() => tasks.filter((t) => t.status === 'done'),  [tasks]);
  const viewTasks     = isArchive ? archivedTasks : activeTasks;

  /* Stats — single pass over the full dataset */
  const stats = useMemo(() => {
    const s = { total: tasks.length, inProgress: 0, overdue: 0, done: 0 };
    for (const t of tasks) {
      if (t.status === 'in_progress') s.inProgress += 1;
      else if (t.status === 'overdue') s.overdue += 1;
      else if (t.status === 'done') s.done += 1;
    }
    return s;
  }, [tasks]);

  /* Filtered count for chip */
  const filteredCount = useMemo(() =>
    viewTasks.filter((t) => {
      const s = statusFilter   === 'all' || t.status   === statusFilter;
      const p = priorityFilter === 'all' || t.priority === priorityFilter;
      const o = ownerFilter    === 'all' || t.owner_id === ownerFilter;
      return s && p && o;
    }).length,
  [viewTasks, statusFilter, priorityFilter, ownerFilter]);

  /* Unique employees for filter */
  const employees = useMemo(() => {
    const seen = new Set();
    const list = [];
    tasks.forEach((t) => {
      if (t.owner?.full_name && !seen.has(t.owner_id)) {
        seen.add(t.owner_id);
        list.push({ id: t.owner_id, name: t.owner.full_name });
      }
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  return (
    <AppShell title="Dashboard" sectionLabel="Workspace">

      {/* ── Stats cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
        <StatCard label="Total tasks"     count={stats.total}      accent="#64748B" />
        <StatCard label="In Progress"     count={stats.inProgress} accent="#0891B2" />
        <StatCard label="Overdue"         count={stats.overdue}    accent="#DC2626" />
        <StatCard label="Done / Archived" count={stats.done}       accent="#16A34A" />
      </div>

      {/* ── Filter row ── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {/* Task count chip */}
        <span className="text-xs font-semibold text-muted bg-canvas border border-border-base rounded-full px-3 py-1 whitespace-nowrap flex-shrink-0">
          {filteredCount} {filteredCount === 1 ? 'task' : 'tasks'}
        </span>

        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {/* Employee / Owner filter */}
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="input !w-auto text-sm py-1.5 pr-8 cursor-pointer"
            aria-label="Filter by employee"
          >
            <option value="all">All employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>

          {/* Status filter — only in active view */}
          {!isArchive && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input !w-auto text-sm py-1.5 pr-8 cursor-pointer"
              aria-label="Filter by status"
            >
              {STATUS_OPTS_ACTIVE.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          {/* Priority filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="input !w-auto text-sm py-1.5 pr-8 cursor-pointer"
            aria-label="Filter by priority"
          >
            {PRIORITY_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Columns toggle */}
          <ColumnsDropdown
            allToggleable={colPrefs.allToggleable}
            visible={colPrefs.visible}
            onToggle={colPrefs.toggleCol}
          />

          {/* Archive / Active toggle */}
          <button
            type="button"
            onClick={() => handleViewSwitch(!isArchive)}
            className={`btn text-sm gap-1.5 ${
              isArchive
                ? 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                : 'border border-border-base bg-card hover:bg-slate-50 text-muted'
            }`}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
            </svg>
            {isArchive ? 'Back to active' : 'Completed Tasks'}
          </button>

          {/* Add task — hidden in archive view */}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="btn-primary text-sm"
          >
            <Icon name="plus" size={15} />
            Add task
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <TaskTable
        tasks={viewTasks}
        loading={loading}
        error={error}
        columns={colPrefs.columns}
        widths={colPrefs.widths}
        onColumnResize={colPrefs.setWidth}
        statusFilter={statusFilter}
        priorityFilter={priorityFilter}
        ownerFilter={ownerFilter}
        onRemark={allowRemark ? setRemarkTask : undefined}
        onDelete={allowDelete ? handleDelete : undefined}
      />

      <AddTaskModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(row) => { if (row) upsertTask(row); }}
      />

      <RemarkModal
        open={!!remarkTask}
        task={remarkTask}
        onClose={() => setRemarkTask(null)}
        onSaved={(id, value) => patchTask(id, { remarks: value })}
      />
    </AppShell>
  );
}
